import React, { useEffect } from "react";
import { useAuth } from "../context/authContext";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import {
  Link as ReactRouterLink,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  Stack,
  useTheme,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useThemeContext } from "../context/themeContext";
import HideOnScroll from "../components/HideOnScroll";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import MobileInstallInstructions from "../components/MobileInstallInstructions";
import { toast } from "react-toastify";
import Socials from "../components/Socials";
import MotionInView from "../components/MotionInView";
import HeroSection from "../features/home/HeroSection.js";
import TestimonialsSection from "../features/home/TestimonialsSection.js";
import TrackSection from "../features/home/TrackSection.js";
import CurateSection from "../features/home/CurateSection.js";
import ExploreSection from "../features/home/ExploreSection.js";
import MissionSection from "../features/home/MissionSection.js";
import AboutSection from "../features/home/AboutSection.js";

/**
 * Rises a homepage section's content in as it scrolls into view, matching the
 * app pages' entrances. Full width and centring, so each section's own
 * max-width grid lays out as it would as the section's direct child.
 */
const SectionReveal = ({ children }) => (
  <MotionInView
    // Waits until the section is a fifth of the way up the screen, and takes
    // longer than the app's card entrances, so it's seen rather than missed
    margin="0px 0px -20% 0px"
    duration={0.6}
    style={{ width: "100%", display: "flex", justifyContent: "center" }}
  >
    {children}
  </MotionInView>
);

/**
 * The public landing page. Signed-in users are redirected to their dashboard
 * unless they arrived from an in-app link (?redirect=false).
 */
export default function Home() {
  const theme = useTheme();
  const { user } = useAuth();

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const { toggleMode, isTouchDevice } = useThemeContext();
  const [openInstallInstructions, setOpenInstallInstructions] =
    React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasRedirectParam = params.has("redirect");
  // ?redirect=false (in-app links to the homepage) is moved into history state
  // below, so it survives Back and closing a modal opened from here
  const skipRedirect =
    params.get("redirect") === "false" || !!location.state?.skipRedirect;

  useEffect(() => {
    const flashMessage = localStorage.getItem("flashMessage");
    const flashType = localStorage.getItem("flashType");

    if (flashMessage) {
      setTimeout(() => {
        toast[flashType || "info"](flashMessage);
        localStorage.removeItem("flashMessage");
        localStorage.removeItem("flashType");
      }, 0);
    }
  }, []);

  useEffect(() => {
    // Tidy the URL through the router, not window.history — a raw
    // replaceState leaves the router's location stale and wipes its history
    // state, so popping back here later reads as a fresh "/" and redirects
    if (hasRedirectParam) {
      navigate(location.pathname, { replace: true, state: { skipRedirect } });
      return;
    }
    // Signed-in users land on their dashboard (Profile's default tab) — this
    // is also where the installed app opens, its start_url being "/". Replace,
    // so Back from the dashboard doesn't bounce off "/" straight back to it.
    if (user && !skipRedirect) {
      navigate(`/u/${user.username}`, { replace: true });
    }
  }, [user, navigate, skipRedirect, hasRedirectParam, location.pathname]);

  return (
    <>
      <HideOnScroll>
        <AppBar
          color="inherit"
          sx={{ backgroundImage: "none", boxShadow: "none" }}
        >
          <Toolbar>
            <Box sx={{ display: "flex", flexGrow: 1 }}>
              <Socials />
            </Box>

            {/* PWA install — only meaningful on installable (touch) devices */}
            {isTouchDevice && (
              <Tooltip title="Install mobile app" placement="left">
                <IconButton
                  onClick={() => setOpenInstallInstructions(true)}
                  aria-label="Install mobile app"
                >
                  <InstallMobileIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip
              title={
                theme.palette.mode === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
              placement="left"
            >
              <IconButton
                onClick={toggleMode}
                sx={{
                  ml: 0.5,
                }}
              >
                {theme.palette.mode === "dark" ? (
                  <LightModeIcon />
                ) : (
                  <DarkModeIcon />
                )}
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
      </HideOnScroll>

      <Dialog
        open={openInstallInstructions}
        onClose={() => setOpenInstallInstructions(false)}
        aria-labelledby="install-mobile-app-dialog"
      >
        <DialogTitle
          id="install-mobile-app-dialog"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <InstallMobileIcon /> Install mobile app
        </DialogTitle>
        <DialogContent>
          <MobileInstallInstructions />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenInstallInstructions(false)}
            variant="contained"
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        id="landing-section"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          gap: 3,
          px: { xs: 2, md: 0 },
        }}
      >
        <HeroSection />
      </Box>

      <Box
        id="testimonials-section"
        sx={{
          display: "flex",
          justifyContent: "center",
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 4, lg: 0 },
          backgroundColor: (theme) => theme.palette.secondary.main,
        }}
      >
        <SectionReveal>
          <TestimonialsSection />
        </SectionReveal>
      </Box>

      <Box
        id="track-section"
        sx={{
          // backgroundColor: (theme) => theme.palette.secondary.main,
          py: 2,
          px: { xs: 2, md: 4, lg: 0 },
          display: "flex",
          justifyContent: "center",
          textAlign: { xs: "center", md: "left" },
        }}
      >
        <TrackSection />
      </Box>

      <Box
        id="curate-section"
        sx={{
          py: 4,
          px: { xs: 2, md: 8, lg: 0 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: (theme) => theme.palette.secondary.main,
        }}
      >
        <CurateSection />
      </Box>

      <Box
        id="explore-section"
        sx={{
          display: "flex",
          justifyContent: "center",
          // backgroundColor: (theme) => theme.palette.secondary.main,
          py: { xs: 4, md: 6 },
          px: { xs: 4, md: 4, lg: 0 },
        }}
      >
        <ExploreSection />
      </Box>

      <Box
        id="mission-section"
        sx={{
          backgroundColor: (theme) => theme.palette.secondary.main,
          px: { xs: 4, md: 4, lg: 0 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        {/* Animates its text and graphic separately (see MissionSection) */}
        <MissionSection />
      </Box>

      <Box
        id="about-section"
        sx={{
          // backgroundColor: (theme) => theme.palette.secondary.main,
          py: 4,
          px: { xs: 4, md: 4, lg: 0 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <SectionReveal>
          <AboutSection />
        </SectionReveal>
      </Box>

      <Box
        id="TMDB-section"
        sx={{
          display: "flex",
          backgroundColor: (theme) => theme.palette.secondary.main,
          justifyContent: "center",
          py: { xs: "50px", md: "50px" },
          px: { xs: 4, md: 4, lg: 0 },
          // textAlign: { xs: "center", md: "left" },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <img
            src="/images/tmdb-logo.png"
            alt="The Movie Database (TMDB) logo"
            width="104px"
            height="75px"
          />
          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              maxWidth: "400px",
            }}
          >
            {" "}
            All movie and TV show metadata used in Curate is provided by The
            Movie Database (TMDB) but is not endorsed, certified, or otherwise
            approved by TMDB.
          </Typography>
        </Stack>
      </Box>

      <Box
        id="footer-section"
        sx={{
          display: "flex",
          backgroundColor: (theme) => theme.palette.secondary.main,
          justifyContent: { xs: "center", md: "flex-end" },
          pr: { xs: 0, md: 2 },
          pb: 2,
          // textAlign: { xs: "center", md: "left" },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontWeight: "normal",
              typography: "body2",
            }}
            href="https://app.youform.com/forms/fghdoj5a"
            target="_blank"
            rel="noopener noreferrer"
          >
            {" "}
            👋 Contact us
          </Button>
          <Button
            component={ReactRouterLink}
            to="/privacy"
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontWeight: "normal",
              typography: "body2",
            }}
          >
            {" "}
            🔒 Privacy policy
          </Button>
          <Button
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontWeight: "normal",
              typography: "body2",
            }}
            onClick={() => scrollToSection("landing-section")}
          >
            {" "}
            👆 Back to top
          </Button>
        </Stack>
      </Box>
    </>
  );
}
